import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import PageAlert from "../../components/PageAlert/PageAlert";
import { resolveApiError } from "../../utils/apiError";
import {
  createArticleCategory,
  deleteArticleCategory,
  getArticleCategories,
  getArticleCategory,
  getCategoryParentId,
  makeSlug,
  normalizePaginated,
  splitArticleCategories,
  updateArticleCategory,
} from "./articleApi";
import "./articles.scss";

const savedRecordId = (record) => record?.id ?? record?.category?.id ?? null;

const emptySubcategory = () => ({
  key: `sub-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  id: null,
  name: "",
  slug: "",
  description: "",
  status: "1",
  sort_order: 0,
});

const mapSubcategory = (item) => ({
  key: `saved-${item.id}`,
  id: item.id,
  name: item.name || "",
  slug: item.slug || "",
  description: item.description || "",
  status: item.status ? "1" : "0",
  sort_order: item.sort_order ?? 0,
});

const ArticleCategoryForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = useMemo(() => Boolean(id), [id]);

  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    status: "1",
    sort_order: 0,
    parent_id: "",
  });
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isSubcategoryForm = Boolean(form.parent_id);

  useEffect(() => {
    if (!location.state?.notice) return;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const applyCategory = useCallback((category, allItems = []) => {
    const parentId = getCategoryParentId(category);
    const { childrenByParent } = splitArticleCategories([category, ...allItems]);
    const children = category.children?.length
      ? category.children
      : childrenByParent.get(Number(category.id)) || [];
    setForm({
      name: category.name || "",
      slug: category.slug || "",
      description: category.description || "",
      status: category.status ? "1" : "0",
      sort_order: category.sort_order ?? 0,
      parent_id: parentId ? String(parentId) : "",
    });
    setSubcategories(children.map(mapSubcategory));
  }, []);

  const loadCategory = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [category, listResponse] = await Promise.all([
        getArticleCategory(id),
        getArticleCategories({ per_page: 200 }),
      ]);
      applyCategory(category, normalizePaginated(listResponse).items);
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setLoading(false);
    }
  }, [applyCategory, id, navigate]);

  useEffect(() => {
    if (isEdit) loadCategory();
  }, [isEdit, loadCategory]);

  const updateField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "name") next.slug = makeSlug(value);
      if (field === "slug") next.slug = makeSlug(value);
      return next;
    });
  };

  const updateSubcategory = (key, field, value) => {
    setSubcategories((prev) =>
      prev.map((item) => {
        if (item.key !== key) return item;
        const next = { ...item, [field]: value };
        if (field === "name") next.slug = makeSlug(value);
        if (field === "slug") next.slug = makeSlug(value);
        return next;
      })
    );
  };

  const addSubcategory = () => {
    setSubcategories((prev) => [...prev, emptySubcategory()]);
  };

  const persistSubcategories = async (parentId) => {
    const rows = subcategories.filter((item) => item.name.trim());
    for (const row of rows) {
      const payload = {
        name: row.name,
        slug: row.slug || makeSlug(row.name),
        description: row.description,
        status: row.status,
        sort_order: Number(row.sort_order) || 0,
        parent_id: Number(parentId),
      };
      if (row.id) await updateArticleCategory(row.id, payload);
      else await createArticleCategory(payload);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }

    const unnamed = subcategories.find((item) => !item.name.trim() && (item.id || item.slug));
    if (unnamed) {
      setError("Each subcategory needs a name.");
      return;
    }

    setSaving(true);
    setError("");
    setNotice("");

    try {
      const payload = {
        name: form.name,
        slug: form.slug || makeSlug(form.name),
        description: form.description,
        status: form.status,
        sort_order: Number(form.sort_order) || 0,
        parent_id: form.parent_id ? Number(form.parent_id) : null,
      };

      const saved = isEdit ? await updateArticleCategory(id, payload) : await createArticleCategory(payload);
      const parentId = isEdit ? id : savedRecordId(saved);

      if (parentId && !payload.parent_id) {
        await persistSubcategories(parentId);
      }

      const message = payload.parent_id
        ? isEdit
          ? "Subcategory details saved."
          : "Subcategory created."
        : isEdit
          ? "Category details saved."
          : "Category created.";

      if (!isEdit) {
        navigate("/admin/articles/categories", { replace: true, state: { notice: message } });
        return;
      }

      const [category, listResponse] = await Promise.all([
        getArticleCategory(id),
        getArticleCategories({ per_page: 200 }),
      ]);
      applyCategory(category, normalizePaginated(listResponse).items);
      setNotice(message);
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (item) => {
    setDeleteError("");
    setPendingDelete(item);
  };

  const closeDelete = () => {
    if (deleting) return;
    setPendingDelete(null);
    setDeleteError("");
  };

  const handleDeleteSubcategory = async () => {
    if (!pendingDelete) return;

    if (!pendingDelete.id) {
      setSubcategories((prev) => prev.filter((item) => item.key !== pendingDelete.key));
      setPendingDelete(null);
      setDeleteError("");
      return;
    }

    setDeleting(true);
    setDeleteError("");
    try {
      await deleteArticleCategory(pendingDelete.id);
      setSubcategories((prev) => prev.filter((item) => item.key !== pendingDelete.key));
      setPendingDelete(null);
      setNotice("Subcategory deleted.");
    } catch (err) {
      resolveApiError(err, setDeleteError, navigate);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="articles-page">
      {notice && <PageAlert variant="success" onDismiss={() => setNotice("")}>{notice}</PageAlert>}
      {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}
      <div className="articles-page__heading">
        <div>
          <h2>{isEdit ? "Edit category" : "New category"}</h2>
          <p>Define a category and optional subcategories used when publishing articles.</p>
        </div>
      </div>

      {loading ? (
        <div className="articles-page__loading">Loading category...</div>
      ) : (
        <form className="articles-page__form articles-page__form--wide articles-page__card" onSubmit={handleSubmit}>
          <label>
            Name
            <input
              value={form.name}
              required
              autoFocus
              onChange={(event) => updateField("name", event.target.value)}
            />
          </label>
          <label>
            Slug
            <input
              value={form.slug}
              onChange={(event) => updateField("slug", event.target.value)}
            />
          </label>
          <label>
            Description
            <textarea
              rows={4}
              value={form.description}
              onChange={(event) => updateField("description", event.target.value)}
            />
          </label>
          <label>
            Status
            <select
              value={form.status}
              onChange={(event) => updateField("status", event.target.value)}
            >
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </label>
          <label>
            Sort order
            <input
              type="number"
              value={form.sort_order}
              onChange={(event) => updateField("sort_order", event.target.value)}
            />
          </label>

          {!isSubcategoryForm && (
            <div className="articles-page__subcategories">
              <div className="articles-page__subcategories-heading">
                <div>
                  <h3>Subcategories</h3>
                  <p>Optional. Each subcategory belongs to this category.</p>
                </div>
                <button className="ui-button" type="button" onClick={addSubcategory}>
                  <Icon icon="ph:plus" height={14} /> Add subcategory
                </button>
              </div>

              {!subcategories.length && (
                <p className="articles-page__empty-inline">No subcategories yet.</p>
              )}

              {subcategories.map((item, index) => (
                <div className="articles-page__subcategory-row" key={item.key}>
                  <div className="articles-page__subcategory-title">
                    <strong>Subcategory {index + 1}</strong>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => openDelete(item)}
                    >
                      Remove
                    </button>
                  </div>
                  <label>
                    Name
                    <input
                      value={item.name}
                      placeholder="e.g. Government Projects"
                      onChange={(event) => updateSubcategory(item.key, "name", event.target.value)}
                    />
                  </label>
                  <label>
                    Slug
                    <input
                      value={item.slug}
                      onChange={(event) => updateSubcategory(item.key, "slug", event.target.value)}
                    />
                  </label>
                  <div className="articles-page__subcategory-grid">
                    <label>
                      Status
                      <select
                        value={item.status}
                        onChange={(event) => updateSubcategory(item.key, "status", event.target.value)}
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </label>
                    <label>
                      Sort order
                      <input
                        type="number"
                        value={item.sort_order}
                        onChange={(event) => updateSubcategory(item.key, "sort_order", event.target.value)}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="articles-page__form-actions">
            <button
              className="ui-button"
              type="button"
              onClick={() => navigate("/admin/articles/categories")}
            >
              Cancel
            </button>
            <button className="ui-button ui-button--primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </form>
      )}
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Remove subcategory"
        message={
          pendingDelete
            ? pendingDelete.id
              ? `Delete “${pendingDelete.name || "this subcategory"}”? It cannot be deleted if it is assigned to an article.`
              : "Remove this unsaved subcategory?"
            : ""
        }
        error={deleteError}
        confirming={deleting}
        onCancel={closeDelete}
        onConfirm={handleDeleteSubcategory}
      />
    </div>
  );
};

export default ArticleCategoryForm;
